import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { config } from "../config";
import type { OriginationSummary } from "../api/deferred-originations";
import { isOriginationComplete } from "../utils/bindingStatus";
import {
  openWidgetSse,
  type WidgetSseConnection,
} from "../utils/widgetSse";
import { getEvents, safeCapture } from "../analytics";

const TERMINAL_BINDING_STATES = new Set<OriginationSummary["bindingState"]>([
  "expired",
  "cancelled",
  "ofac_rejected",
]);

const TERMINAL_OPERATION_STATUSES = new Set(["completed", "failed", "cancelled"]);

export function isOriginationNonTerminal(item: OriginationSummary): boolean {
  if (TERMINAL_BINDING_STATES.has(item.bindingState)) {
    return false;
  }
  if (item.operationStatus) {
    if (isOriginationComplete(item.bindingState, item.operationStatus)) {
      return false;
    }
    if (TERMINAL_OPERATION_STATUSES.has(item.operationStatus)) {
      return false;
    }
  }
  return true;
}

export function hasNonTerminalOriginations(
  items: OriginationSummary[]
): boolean {
  return items.some(isOriginationNonTerminal);
}

export function upsertOriginationInList(
  list: OriginationSummary[],
  item: OriginationSummary
): OriginationSummary[] {
  const idx = list.findIndex((o) => o.originationId === item.originationId);
  if (idx >= 0) {
    const next = [...list];
    next[idx] = item;
    return next;
  }
  return [item, ...list];
}

function closeSse(
  conn: WidgetSseConnection | null,
  connRef: MutableRefObject<WidgetSseConnection | null>
): void {
  if (!conn) return;
  conn.close();
  if (connRef.current === conn) {
    connRef.current = null;
  }
}

export interface UseOriginationSSEOptions {
  watchOriginationId?: string;
}

/**
 * Keeps deferred payout origination statuses live via Server-Sent Events.
 */
export function useOriginationSSE(
  linkId: string | null | undefined,
  originations: OriginationSummary[],
  onUpsert: (item: OriginationSummary) => void,
  onReconnect?: () => void,
  options?: UseOriginationSSEOptions
): void {
  const watchOriginationId = options?.watchOriginationId;
  const connRef = useRef<WidgetSseConnection | null>(null);
  const isFirstOpenRef = useRef(true);

  const originationsRef = useRef(originations);
  const onUpsertRef = useRef(onUpsert);
  const onReconnectRef = useRef(onReconnect);
  originationsRef.current = originations;
  onUpsertRef.current = onUpsert;
  onReconnectRef.current = onReconnect;

  const hasActiveOriginations = useMemo(() => {
    if (!watchOriginationId) {
      return hasNonTerminalOriginations(originations);
    }
    const item = originations.find(
      (o) => o.originationId === watchOriginationId
    );
    return item ? isOriginationNonTerminal(item) : false;
  }, [originations, watchOriginationId]);

  useEffect(() => {
    if (!linkId || !hasActiveOriginations) {
      closeSse(connRef.current, connRef);
      return;
    }

    if (connRef.current) return;

    isFirstOpenRef.current = true;
    const url = `${config.API_BASE_URL}/widget/links/${linkId}/deferred-payouts/sse`;

    const maybeClose = (): void => {
      const items = originationsRef.current;
      if (watchOriginationId) {
        const item = items.find((o) => o.originationId === watchOriginationId);
        if (!item || !isOriginationNonTerminal(item)) {
          closeSse(connRef.current, connRef);
        }
        return;
      }
      if (!hasNonTerminalOriginations(items)) {
        closeSse(connRef.current, connRef);
      }
    };

    const conn = openWidgetSse({
      url,
      onOpen: () => {
        if (!isFirstOpenRef.current) {
          onReconnectRef.current?.();
        }
        isFirstOpenRef.current = false;
      },
      onMessage: (event) => {
        try {
          const raw = event.data;
          const data = typeof raw === "string" ? JSON.parse(raw) : raw;

          if (data?.type === "heartbeat") {
            maybeClose();
            return;
          }

          const updated = data as OriginationSummary;
          if (!updated?.originationId) return;

          if (
            watchOriginationId &&
            updated.originationId !== watchOriginationId
          ) {
            maybeClose();
            return;
          }

          safeCapture(getEvents().REQUEST_STATUS_CHANGED, {
            origination_id: updated.originationId,
            status: updated.bindingState,
            operation_id: updated.operationId,
            operation_status: updated.operationStatus,
            funding_status: updated.fundingStatus,
            send_amount: updated.sendAmount,
          });

          onUpsertRef.current(updated);
          maybeClose();
        } catch (e) {
          console.error("[useOriginationSSE] message parse error", e);
        }
      },
      onError: () => {
        console.warn("[useOriginationSSE] connection issue (auto-retry)", linkId);
      },
    });
    connRef.current = conn;

    return () => {
      closeSse(conn, connRef);
    };
  }, [linkId, hasActiveOriginations, watchOriginationId]);
}
